import React from 'react';
import {
  Drawer,
  Toolbar,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Badge,
  styled,
  alpha,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Tooltip } from '@/components/ui';
import type { AppSidebarProps, MenuItem } from './types';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

// Styled components for clean UI
const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) =>
    prop !== 'collapsed' && prop !== 'drawerwidth' && prop !== 'collapsedwidth',
})<{
  collapsed?: boolean;
  drawerwidth: number;
  collapsedwidth: number;
}>(({ theme, collapsed, drawerwidth, collapsedwidth }) => ({
  width: collapsed ? collapsedwidth : drawerwidth,
  flexShrink: 0,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  '& .MuiDrawer-paper': {
    width: collapsed ? collapsedwidth : drawerwidth,
    boxSizing: 'border-box',
    backgroundColor: 'rgb(252, 252, 255)',
    borderRight: 'none',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
  },
}));

const LogoContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'rgb(252, 252, 255)',
  color: '#fff',
  justifyContent: 'center',
  minHeight: '30px !important',
  position: 'relative',
  padding: '13px',
  borderBottom: '1px solid #ECEFF2',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 16,
    right: 16,
    height: '1px',
    background: alpha('#ECEFF2', 0.1),
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '56px !important',
    padding: '8px 13px',
    '&::after': {
      left: 8,
      right: 8,
    },
  },
}));

const MenuContainer = styled(Box)(({ theme }) => ({
  overflow: 'auto',
  borderRight: '1px solid #ECEFF2',
  paddingBottom: theme.spacing(2),
  height: '100%',
  color: alpha('#fff', 0.7),
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha('#c41913ff', 0.1),
    borderRadius: 4,
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(1),
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "level",
})<{ active?: boolean; level?: number }>(({ theme, active, level = 0 }) => ({
  margin: level === 0 ? theme.spacing(0.5, 0, 0, 1) : theme.spacing(0.5, 0),
  borderRadius: level === 0 ? 0 : 0,
  padding: level === 0 ? theme.spacing(0.75, 1) : theme.spacing(0.5, 2, 0.5, 4),
  minHeight: level === 0 ? 50 : 32,
  display: "flex",
  alignItems: "center",
  transition: theme.transitions.create(["background-color", "color", "border-radius"], {
    duration: theme.transitions.duration.standard,
  }),
  position: "relative",
  overflow: "hidden",
  color: "rgb(40, 42, 48)",

  "&:hover": {
    ...(level === 0
      ? {
        color: "rgb(40, 42, 48)",
        backgroundColor: "transparent",
        "& .MuiListItemIcon-root": {
          "& .MuiSvgIcon-root": {
            color: "rgb(40, 42, 48)",
          },
        },
        "& .MuiListItemText-primary": {
          color: "rgb(40, 42, 48)",
          transform: "translateX(5px)",
        },
      }
      : {
        backgroundColor: "transparent",
        color: "rgb(40, 42, 48)",
        "& .MuiListItemText-primary": {
          color: "rgb(40, 42, 48)",
          transform: "translateX(5px)",
        },
      }),
  },

  ...(active && {
    ...(level === 0 && {
      
      backgroundColor: "rgb(246, 247, 250) !important",
      borderLeft: "2px solid #85BA40 !important",
      
      //borderRadius: "50px 0 0 50px",
      "& .MuiListItemIcon-root": {
        backgroundColor: "rgb(246, 247, 250)",
        width: "36px",
        height: "36px",
        minWidth: "36px",
        color: "#85BA40",
        "& .MuiSvgIcon-root": {
          color: "#85BA40",
        },
      },
      "& .MuiListItemText-primary": {
        color: "#85BA40",
      },
    }),
    ...(level > 0 && {
      backgroundColor: "transparent",
      color: "rgb(40, 42, 48)",
    }),
  }),
}));


const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 36,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
  transition: theme.transitions.create(
    ['color', 'background-color', 'border-radius', 'width', 'height'],
    {
      duration: theme.transitions.duration.standard,
    }
  ),
  marginRight: '10px',

  '& .MuiSvgIcon-root': {
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  margin: 0,

  '& .MuiTypography-root': {
    fontSize: '0.9rem',
    fontWeight: 400,
    letterSpacing: '0.2px',
    transition: theme.transitions.create(['color', 'font-weight', 'transform'], {
      duration: theme.transitions.duration.standard,
    }),
    color: 'inherit',
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontSize: '0.625rem',
    height: 16,
    minWidth: 16,
    padding: '0 4px',
    fontWeight: 600,
  },
}));

export const AppSidebar: React.FC<AppSidebarProps> = ({
  width = 240,
  collapsedWidth = 64,
  collapsed = false,
  logo,
  menuItems,
  currentPath = '',
  onNavigate,
  variant = 'permanent',
  open = true,
  onClose,
  sx,
}) => {
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  const { user } = useAuth();

  const toggleExpanded = (itemId: string) => {
    if (collapsed) return; // Don't expand when collapsed

    setExpandedItems(prev => {
      // If this item is already expanded, just close it
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      // Otherwise, expand this item and close all others
      else {
        return [itemId]; // Only keep the current item expanded, close all others
      }
    });
  };

  // Find parent item ID for the current path
  const findParentForPath = React.useCallback((path: string, items: MenuItem[]): string | null => {
    for (const item of items) {
      // Check if this item has children and if the path matches any child
      if (item.children && item.children.length > 0) {
        // Check if path directly matches a child
        const matchingChild = item.children.find(child => child.path === path);
        if (matchingChild) {
          return item.id; // Return this parent's ID
        }

        // Recursively check deeper levels
        const deeperParent = findParentForPath(path, item.children);
        if (deeperParent) {
          return deeperParent;
        }
      }
    }
    return null;
  }, []);

  // Auto-expand parent menu when a child is selected
  React.useEffect(() => {
    const parentId = findParentForPath(currentPath, menuItems);
    if (parentId) {
      setExpandedItems([parentId]);
    }
  }, [currentPath, menuItems, findParentForPath]);

  const isItemSelected = (item: MenuItem): boolean => {
    if (item.path === currentPath) return true;
    if (item.children) {
      return item.children.some(child => isItemSelected(child));
    }
    return false;
  };

  const isParentExpanded = (itemId: string): boolean => {
    return !collapsed && expandedItems.includes(itemId);
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    // Check if this item is directly selected
    const isDirectlySelected = item.path === currentPath;
    // Check if it's selected because it has selected children
    const hasSelectedChild = hasChildren && item.children!.some(child => isItemSelected(child));
    // Item is selected if it's directly selected or has selected children
    const isSelected = !hasChildren && isDirectlySelected;
    const isParentSelected = hasChildren && (isDirectlySelected || hasSelectedChild);
    const isExpanded = isParentExpanded(item.id);

    const menuButton = (
      <StyledListItemButton
        active={isSelected || isParentSelected}
        level={level}
        disabled={item.disabled}
        onClick={() => {
          if (hasChildren) {
            toggleExpanded(item.id);
          } else if (onNavigate && !item.disabled) {
            onNavigate(item.path);
          }
        }}
        sx={{
          pl: collapsed ? 1.5 : undefined,
          pr: collapsed ? 1.5 : undefined,
          justifyContent: collapsed ? 'center' : 'flex-start',
          minWidth: collapsed ? 40 : 'auto',
          borderRadius: collapsed ? '0%' : undefined,
          margin: collapsed ? '8px auto' : undefined,
          height: collapsed ? 40 : undefined,
          width: collapsed ? 40 : undefined,
          borderLeft: (isSelected || isParentSelected) ? '3px solid red' : 'none',
          ...(collapsed && (isSelected || isParentSelected)
            ? {
              backgroundColor: 'rgb(40, 42, 48)', // White background for selected items in collapsed view
              '& .MuiSvgIcon-root': {
                color: 'rgba(30, 18, 1, 1)', // Change icon color to blue when highlighted
              },
            }
            : {}),

          // Simple hover effect for collapsed menu items
          '&:hover': {
            ...(collapsed
              ? {
                backgroundColor: 'transparent',
                '& .MuiSvgIcon-root': {
                  color: 'rgb(40, 42, 48)',
                  transform: 'scale(1.1)',
                },
              }
              : {}),
          },
        }}
      >
        {level > 0 && !collapsed && (
          <Box
            component="span"
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: isSelected ? '#fff' : 'rgba(255,255,255,0.5)',
              display: 'inline-block',
              mr: 2.6,
              ml: 1,
            }}
          />
        )}

        {(!collapsed || level === 0) && (
          <StyledListItemIcon
            sx={{
              ...(level > 0 && { display: 'none' }),
              minWidth: collapsed ? 36 : 36,
              marginRight: collapsed ? 0 : undefined,
            }}
          >
            {item.badge ? (
              <StyledBadge
                badgeContent={item.badge}
                variant={typeof item.badge === 'string' ? 'dot' : 'standard'}
                invisible={collapsed && typeof item.badge === 'string'}
              >
                {item.icon}
              </StyledBadge>
            ) : (
              item.icon
            )}
          </StyledListItemIcon>
        )}

        {!collapsed && (
          <>
            <StyledListItemText
              primary={item.text}
              sx={{
                '& .MuiTypography-root': {
                  fontSize: level > 0 ? '0.8125rem' : '0.875rem',
                  fontWeight: level > 0 ? 400 : 500,
                  color:
                    level === 0 && (isSelected || isParentSelected)
                      ? '#85BA40' // Selected green for active item (matches icon)
                      : level > 0 && isSelected
                        ? '#85BA40' // Selected green for active child
                        : 'rgb(40, 42, 48)', // Default for non-active items
                },
              }}
            />

            {hasChildren && level === 0 && (
              <Box
                sx={{
                  ml: 1,
                  color: 'inherit',
                  fontSize: '0.75rem',
                }}
              >
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </Box>
            )}
          </>
        )}
      </StyledListItemButton>
    );

    return (
      <React.Fragment key={item.id}>
        <ListItem
          disablePadding
          sx={{
            ...(collapsed && {
              px: 0,
              py: 0.5,
            }),
          }}
        >
          {collapsed ? (
            <Tooltip title={item.text} placement="right" variant="dark">
              {menuButton}
            </Tooltip>
          ) : (
            menuButton
          )}
        </ListItem>

        {/* Children items */}
        {hasChildren && (
          <>
            {/* When expanded, show children below */}
            {!collapsed && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  sx={{
                    mt: 0,
                    mb: 0,
                    // Removed background color for submenu items
                  }}
                >
                  {item.children!.map(child => renderMenuItem(child, level + 1))}
                </List>
              </Collapse>
            )}

            {/* When collapsed, show children in tooltip */}
            {collapsed && (
              <Box sx={{ display: 'none' }}>
                {/* This ensures children are loaded but not displayed when collapsed */}
                {item.children!.map(child => (
                  <Box key={child.id} sx={{ display: 'none' }} />
                ))}
              </Box>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  // Filter out "Teams" menu for Merchant_team_user
  const filteredMenuItems = React.useMemo(() => {
    if (user?.role === 'MERCHANT_TEAM_USER') {
      return menuItems.map(item => {

        if (item.id === 'merchant' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => child.id !== 'merchant-teams'),
          };
        }
        return item;
      });
    }
    return menuItems;
  }, [menuItems, user]);

  const drawerContent = (
    <>
      {/* Logo Section */}
      {logo && (
        <LogoContainer
          sx={{
            minHeight: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: 3,
            pt: 2,
          }}
        >
          {collapsed ? (
            <Tooltip title="GrowthBnk" placement="right" variant="dark">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src="/logos/logo.png" alt={logo.alt} style={{ height: 30, width: 'auto' }} />
              </Box>
            </Tooltip>
          ) : (
            <img src={logo.src} alt={logo.alt} style={{ width: '115px' }} />
          )}
        </LogoContainer>
      )}

      {/* Menu Items */}
      <MenuContainer sx={{ pt: 2 }}>
        <List sx={{ py: 0 }}>
          {/* {menuItems.map(item => renderMenuItem(item))} */}
          {filteredMenuItems.map(item => renderMenuItem(item))}
        </List>

      </MenuContainer>
    </>
  );

  return (
    <StyledDrawer
      collapsed={collapsed}
      drawerwidth={width}
      collapsedwidth={collapsedWidth}
      variant={variant}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={sx}
    >
      {drawerContent}
    </StyledDrawer>
  );
};
